import { Component, OnDestroy } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { UserProxy } from './core/store/user/user.proxy';
import { dropMenu } from './shared/animations/drop-menu.animation';
import { USER_ROLES, ApiService } from './core/services/api.service';
import { AuthService } from './core/auth/auth.service';
import { HelperService } from './core/services/helper.service';
import { BehaviorSubject, timer } from 'rxjs';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  animations: [dropMenu],
})
export class AppComponent implements OnDestroy {

  USER_ROLES = USER_ROLES;
  showMenu = false;
  showPublicMenu = false;
  isOnboarding$ = this.userProxy.isOnboarding$;
  isEmailPolicyBreak$ = this.userProxy.isEmailPolicyBreak$;
  isLoggedin$ = this.authService.isAuthenticatedSubject$;
  isMaintenance$ = this.userProxy.isMaintenance$;
  isBlocked$ = this.userProxy.isBlocked$;
  isIncognito$ = this.userProxy.userIncognito$;
  incognitoNotify$ = this.userProxy.incognitoNotify$;
  showProfileMenu = false;
  userAvatar$ = this.userProxy.userAvatar$
  role$ = this.userProxy.currentRole$;
  name$ = this.userProxy.userName$;
  email$ = this.userProxy.userEmail$;
  messageCount$ = this.userProxy.messageCount$;
  currentYear = new Date().getFullYear();
  hideHeaderFooter$ = this.helperService.hideHeaderFooter$;
  ssrCheck = false;
  isWelcomePage: boolean = false;
  showImpersonateDialog = false;
  impersonateEmail: string = '';
  isAdmin$ = this.userProxy.isAdmin$;
  isAccountAdmin$ = this.userProxy.isAccountAdmin$;
  apiVersion: string = '1.0.0'; // Default API version
  appVersion: string = '1.2.29'; // Default app version
  
  socialMediaUrls = {
    facebook: 'https://www.facebook.com/people/Atalef-ai/100089987534149/',
    instagram: 'https://www.instagram.com/p/DO-gaIbDUXO/',
    telegram: '',
    linkedin: 'https://www.linkedin.com/company/atalef-ai/'
  };
  
  emailForm = new FormGroup({
    email: new FormControl('', [
      Validators.required,
      Validators.email
    ])
  });
  isSubmitted = false; // For form validation state
  
  // Offline detection
  private isOfflineSubject = new BehaviorSubject<boolean>(false);
  
  /**
   * Set the offline state from child components
   * @param isOffline - The new offline state
   */
  setOffline(isOffline: boolean): void {
    this.isOfflineSubject.next(isOffline);
  }
  isOffline$ = this.isOfflineSubject.asObservable();
  private connectivityCheckInterval = 30000; // Check every 30 seconds
  
  // Getter for the email form control with proper typing
  get emailControl(): FormControl {
    return this.emailForm.get('email') as FormControl;
  }

  // Combined version string for display
  get versionString(): string {
    return `${this.apiVersion} | ${this.appVersion}`;
  }

  constructor(
    private router: Router,
    private authService: AuthService,
    private userProxy: UserProxy,
    private helperService: HelperService,
    private apiService: ApiService
  ) {
    this.helperService.initHelpers();
    this.getAppVersion();
  }

  private getAppVersion() {
    if (this.helperService.isBrowser()) {
      // Get API version and check connectivity
      this.checkServerConnectivity();
      // Set up periodic connectivity checks
      this.startConnectivityMonitoring();
    }
  }

  checkServerConnectivity() {
    this.apiService.getVersion().subscribe({
      next: (version) => {
        if (version) {
          this.apiVersion = version;
          this.isOfflineSubject.next(false); // Server is online
          this.updateAppVersion();
        }
      },
      error: (err) => {
        console.error('Failed to fetch API version - Server appears to be offline:', err);
        this.isOfflineSubject.next(true); // Server is offline
        this.updateAppVersion();
      }
    });
  }

  private startConnectivityMonitoring() {
    // Check connectivity periodically
    timer(this.connectivityCheckInterval, this.connectivityCheckInterval).subscribe(() => {
      this.checkServerConnectivity();
    });
  }

  private updateAppVersion() {
    try {
      // Get app version from meta tag
      const meta = document.querySelector('meta[name="version"]');
      if (meta) {
        this.appVersion = meta.getAttribute('content') || this.appVersion;
      }
    } catch (err) {
      console.error('Failed to get app version from meta tag:', err);
    }
  }

  ngOnInit(): void {
    if (!this.ssrCheck && this.helperService.isBrowser()) {
      this.ssrCheck = true;
    }
    this.router.events.subscribe(() => {
      this.isWelcomePage = this.router.url.includes('onboard');
    });
  }

  toggleProfileMenu(param: boolean) {
    this.showProfileMenu = param;
  }

  logout() {
    this.authService.logout();
  }

  toggleMenu() {
    this.showMenu = !this.showMenu;
    this.helperService.blockScroll$.next(this.showMenu);
  }

  togglePublicMenu() {
    this.showPublicMenu = !this.showPublicMenu;
    this.helperService.blockScroll$.next(this.showPublicMenu);
  }

  signIn() {
    if (this.router.url && this.router.url.includes('/sl/')) {
      localStorage.setItem('slRedirect', this.router.url);
    }
    this.authService.login('/');
  }

  ngOnDestroy(): void {
    // Clean up subscriptions if needed
    this.isOfflineSubject.complete();
  }

  incognitoNotifyConfirm(e) {
    this.incognitoNotify$.next({ result: e ? true : false });
  }

  startImpersonation() {
    this.isSubmitted = true;
    
    if (this.emailForm.valid && this.emailForm.value.email) {
      if (sessionStorage){
      sessionStorage.setItem('Atalef:Impersonate', this.emailForm.value.email);
      }
      window.location.reload();
    }
  }

  onImpersonationDialogClose() {
    this.showImpersonateDialog = false;
    this.emailForm.reset();
    this.isSubmitted = false;
  }

  stopImpersonation() {
    if (sessionStorage){
    sessionStorage.removeItem('Atalef:Impersonate');
    }
    window.location.reload();
  }

  isImpersonating(): boolean {
    if (sessionStorage){
    return !!sessionStorage.getItem('Atalef:Impersonate');
    }
    return false;
  }
}
